import React from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import SEO, { SITE_URL } from "@/components/seo/SEO";
import { useI18n } from "@/i18n/I18nContext";
import { API } from "@/lib/api";
import { BLOG_ARTICLES } from "@/data/blogArticles";
import { buildBreadcrumbSchema } from "@/lib/seoSchemas";
import { BLOG } from "@/constants/testIds";

const CATEGORY_LABELS = {
  fr: { guide: "Guide", comparatif: "Comparatif", usage: "Usage", mariage: "Mariage", anniversaire: "Anniversaire", famille: "Famille", amour: "Amour", hommage: "Hommage", occasion: "Occasions", tendances: "Tendances" },
  en: { guide: "Guide", comparatif: "Comparison", usage: "Use case", mariage: "Wedding", anniversaire: "Birthday", famille: "Family", amour: "Love", hommage: "Tribute", occasion: "Occasions", tendances: "Trends" },
};

export default function BlogList() {
  const { language } = useI18n();
  const isEn = language === "en";
  const siteUrl = SITE_URL;
  // Articles dynamiques (générés chaque jour côté serveur) fusionnés avec les 30 statiques.
  const [dynamicPosts, setDynamicPosts] = React.useState([]);
  React.useEffect(() => {
    fetch(`${API}/blog/posts`)
      .then((r) => (r.ok ? r.json() : { posts: [] }))
      .then((d) => setDynamicPosts(d.posts || []))
      .catch(() => {});
  }, []);
  const staticSlugs = new Set(BLOG_ARTICLES.map((a) => a.slug));
  const articles = [...BLOG_ARTICLES, ...dynamicPosts.filter((p) => !staticSlugs.has(p.slug))]
    .sort((a, b) => new Date(b.publishedDate) - new Date(a.publishedDate));
  const labels = CATEGORY_LABELS[isEn ? "en" : "fr"];

  const jsonLd = buildBreadcrumbSchema(
    [{ name: isEn ? "Home" : "Accueil", path: "/" }, { name: "Blog", path: "/blog" }],
    siteUrl
  );

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={isEn ? "Kalypso Music Blog - AI-Generated Music and Songs" : "Blog Kalypso Music - Musique et Chansons Générées par IA"}
        description={isEn
          ? "Guides, comparisons and tips on AI music creation: how to create a song, generate a singing voice, choose the right AI tool, and gift personalized music."
          : "Guides, comparatifs et conseils sur la création musicale par intelligence artificielle : comment créer une chanson, générer une voix chantée, choisir le bon outil IA."}
        path="/blog"
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="mx-auto max-w-5xl px-4 py-20 sm:px-6">
        <h1 className="font-heading text-3xl font-medium tracking-tight sm:text-4xl">Blog Kalypso Music</h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          {isEn
            ? "Guides, comparisons and tips on AI music creation, with real song examples for every occasion."
            : "Guides, comparatifs et conseils sur la création musicale par intelligence artificielle, avec de vrais exemples de chansons pour chaque occasion."}
        </p>
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2">
          {articles.map((article) => {
            const content = article[language] || article.fr;
            return (
              <Link
                key={article.slug}
                to={`/blog/${article.slug}`}
                data-testid={BLOG.listItem(article.slug)}
                className="group rounded-2xl border border-border bg-card p-6 transition-colors hover:border-primary/40"
              >
                <span className="inline-flex rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                  {labels[article.category] || article.category}
                </span>
                <h2 className="mt-4 font-heading text-lg font-medium leading-snug group-hover:text-primary">{content.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{content.excerpt}</p>
                <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{new Date(article.publishedDate).toLocaleDateString(isEn ? "en-US" : "fr-FR")}</span>
                  <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{content.readingTime} min</span>
                </div>
              </Link>
            );
          })}
        </div>
      </main>
      <Footer />
    </div>
  );
}
